import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsAttachmentDownloadDisabled } from '@confluence/data-security/entry-points/useIsAttachmentDownloadDisabled.ts';

import type {
	AttachmentItem,
	AttachmentsMacroParams,
	AttachmentSortBy,
	AttachmentSortOrder,
	ResolvedAttachmentsMacroParams,
} from '../attachmentsMacroTypes.ts';
import { toBareContentId } from '../downloadUrl.ts';
import { normalizeAttachment } from '../normalization/normalizeAttachment.ts';

import type {
	useAttachmentsMacroDataQuery$data,
	useAttachmentsMacroDataQuery$variables,
} from './__generated__/useAttachmentsMacroDataQuery.graphql.ts';
import { USE_ATTACHMENTS_MACRO_DATA_QUERY } from './useAttachmentsMacroDataQuery.ts';
import type {
	useAttachmentsPageByTitleQuery$data,
	useAttachmentsPageByTitleQuery$variables,
} from './__generated__/useAttachmentsPageByTitleQuery.graphql.ts';
import {
	parsePageRef,
	USE_ATTACHMENTS_PAGE_BY_TITLE_QUERY,
} from './useAttachmentsPageByTitleQuery.ts';

const DEFAULT_PAGE_SIZE = 500;
const MAX_PAGE_SIZE = 500;

export const resolveParams = (params: AttachmentsMacroParams): ResolvedAttachmentsMacroParams => {
	const patternsRaw = params.patterns?.value ?? '';
	const labelsRaw = params.labels?.value ?? '';

	const patterns = patternsRaw
		? patternsRaw.split(',').reduce<string[]>((acc, p) => {
				const trimmed = p.trim();
				if (trimmed) acc.push(trimmed);
				return acc;
			}, [])
		: [];

	const labels = labelsRaw
		? labelsRaw.split(',').reduce<string[]>((acc, l) => {
				const trimmed = l.trim();
				if (trimmed) acc.push(trimmed);
				return acc;
			}, [])
		: [];

	const showVersionHistory = params.old?.value !== 'false';
	const showUpload = params.upload?.value !== 'false';
	const showPreview = params.preview?.value === 'true';

	// The editor panel sends "Sort by" as `sort`; accept both.
	const sortByRaw = params.sortBy?.value ?? params.sort?.value;
	const sortBy: AttachmentSortBy =
		sortByRaw === 'date' ||
		sortByRaw === 'createddate' ||
		sortByRaw === 'size' ||
		sortByRaw === 'name'
			? sortByRaw
			: 'date';

	const sortOrderRaw = params.sortOrder?.value;
	const sortOrder: AttachmentSortOrder =
		sortOrderRaw === 'ascending' || sortOrderRaw === 'descending' ? sortOrderRaw : 'ascending';

	// The editor panel sends "Number of items to display" as `num`; accept both.
	const pageSizeRaw = parseInt(params.pageSize?.value ?? params.num?.value ?? '', 10);
	const pageSize =
		!isNaN(pageSizeRaw) && pageSizeRaw > 0
			? Math.min(pageSizeRaw, MAX_PAGE_SIZE)
			: DEFAULT_PAGE_SIZE;

	return {
		patterns,
		labels,
		showVersionHistory,
		sortBy,
		sortOrder,
		showUpload,
		showPreview,
		pageRef: params.page?.value ?? undefined,
		pageSize,
	};
};

const matchesPatterns = (fileName: string, patterns: string[]): boolean => {
	if (patterns.length === 0) return true;
	return patterns.some((rawPattern) => {
		try {
			const pattern = rawPattern.startsWith('*') ? '.' + rawPattern : rawPattern;
			return new RegExp(`^(?:${pattern})$`).test(fileName);
		} catch {
			return false;
		}
	});
};

// AND logic: attachment must have ALL listed labels.
const matchesLabels = (attachmentLabels: string[], requiredLabels: string[]): boolean => {
	if (requiredLabels.length === 0) return true;
	const labelSet = new Set(attachmentLabels);
	return requiredLabels.every((label) => labelSet.has(label));
};

const sortAttachments = (
	attachments: AttachmentItem[],
	sortBy: AttachmentSortBy,
	sortOrder: AttachmentSortOrder,
): AttachmentItem[] => {
	return [...attachments].sort((a, b) => {
		let comparison = 0;
		switch (sortBy) {
			case 'date':
				comparison =
					new Date(a.lastModificationDate).getTime() - new Date(b.lastModificationDate).getTime();
				break;
			case 'createddate':
				comparison = new Date(a.creationDate).getTime() - new Date(b.creationDate).getTime();
				break;
			case 'size':
				comparison = a.fileSize - b.fileSize;
				break;
			case 'name':
				comparison = a.fileName.toLowerCase().localeCompare(b.fileName.toLowerCase());
				break;
		}
		return sortOrder === 'descending' ? -comparison : comparison;
	});
};

export type UseAttachmentsMacroDataResult = {
	attachments: AttachmentItem[];
	totalCount: number;
	currentPage: number;
	totalPages: number;
	hasNextPage: boolean;
	pageId: string;
	dspBlocksDownloads: boolean;
	hasAttachFilePermission: boolean;
	loading: boolean;
	error: Error | null;
	sortBy: AttachmentSortBy;
	sortOrder: AttachmentSortOrder;
	onSort: (column: AttachmentSortBy) => void;
	onPageChange: (page: number) => void;
	showVersionHistory: boolean;
	showUpload: boolean;
	showPreview: boolean;
	refetch: () => void;
};

export const useAttachmentsMacroData = (
	params: AttachmentsMacroParams,
	contentId?: string,
	hostSpaceKey?: string,
): UseAttachmentsMacroDataResult => {
	const resolved = useMemo(() => resolveParams(params), [params]);

	// Cross-page mode: resolve the target page's id by title.
	// Uses a dual-query fallback: both "split on colon" and "raw bare title" interpretations
	// are fetched in one request. Split is preferred; raw is the fallback (handles colons in titles).
	const crossPageTarget = useMemo(
		() => (resolved.pageRef ? parsePageRef(resolved.pageRef, hostSpaceKey) : null),
		[resolved.pageRef, hostSpaceKey],
	);
	const isCrossPage = !!crossPageTarget?.rawTitle && !!crossPageTarget?.rawSpaceKey;

	const titleQuery = useQueryInterop<
		useAttachmentsPageByTitleQuery$data,
		useAttachmentsPageByTitleQuery$variables
	>(USE_ATTACHMENTS_PAGE_BY_TITLE_QUERY, {
		variables: {
			rawSpaceKey: crossPageTarget?.rawSpaceKey ?? '',
			rawTitle: crossPageTarget?.rawTitle ?? '',
			splitSpaceKey: crossPageTarget?.splitSpaceKey ?? '',
			splitTitle: crossPageTarget?.splitTitle ?? '',
			hasSplit: crossPageTarget?.hasSplit ?? false,
		},
		fetchPolicy: 'cache-first',
		skip: !isCrossPage,
	});

	// Resolution: prefer split (cross-space) result; fall back to raw (bare title in host space).
	// Case-insensitive title match (legacy is case-insensitive).
	const splitNode = titleQuery.data?.splitContent?.nodes?.[0];
	const rawNode = titleQuery.data?.rawContent?.nodes?.[0];

	const crossPageResolvedId = useMemo(() => {
		// Try split interpretation first (e.g. "DEV:My Page" → space DEV, title "My Page")
		if (
			splitNode?.title &&
			crossPageTarget?.splitTitle &&
			splitNode.title.toLowerCase() === crossPageTarget.splitTitle.toLowerCase()
		) {
			return splitNode.id ?? null;
		}
		if (
			rawNode?.title &&
			crossPageTarget?.rawTitle &&
			rawNode.title.toLowerCase() === crossPageTarget.rawTitle.toLowerCase()
		) {
			return rawNode.id ?? null;
		}
		return null;
	}, [splitNode, rawNode, crossPageTarget]);

	const crossPageNotFound = isCrossPage && !titleQuery.loading && !crossPageResolvedId;

	const effectiveContentId = isCrossPage ? crossPageResolvedId : (contentId ?? null);

	const [sortBy, setSortBy] = useState<AttachmentSortBy>(resolved.sortBy);
	const [sortOrder, setSortOrder] = useState<AttachmentSortOrder>(resolved.sortOrder);
	const [currentPage, setCurrentPage] = useState(1);

	// Sync sort state when macro params change (e.g. editor panel dropdown).
	useEffect(() => {
		setSortBy(resolved.sortBy);
		setSortOrder(resolved.sortOrder);
		setCurrentPage(1);
	}, [resolved.sortBy, resolved.sortOrder]);

	// Always fetch all attachments and paginate client-side, matching legacy behavior.
	// This gives us the true total upfront so all page numbers render immediately.
	const { data, loading, error, refetch } = useQueryInterop<
		useAttachmentsMacroDataQuery$data,
		useAttachmentsMacroDataQuery$variables
	>(USE_ATTACHMENTS_MACRO_DATA_QUERY, {
		variables: {
			contentId: effectiveContentId ?? '',
			first: DEFAULT_PAGE_SIZE,
			offset: 0,
		},
		fetchPolicy: 'cache-first',
		skip: !effectiveContentId,
	});

	// DSP check — defaults to false so downloads aren't transiently hidden.
	const { isDisabled: dspBlocksDownloads = false } = useIsAttachmentDownloadDisabled({
		contentId: effectiveContentId ?? '',
		skip: !effectiveContentId,
	});

	const rawPageId = data?.singleContent?.id ?? '';
	// AGG returns ARI format (ari:cloud:confluence::content/12345); download URLs need bare numeric ID.
	const pageId = toBareContentId(rawPageId);

	const hasAttachFilePermission =
		data?.singleContent?.operations?.some(
			(o) => o?.operation?.toLowerCase() === 'create_attachment',
		) ?? false;

	// Normalize raw GraphQL nodes; bad/redacted nodes are dropped.
	const allAttachments = useMemo(() => {
		const nodes = data?.singleContent?.attachments?.nodes ?? [];
		return nodes.flatMap<AttachmentItem>((node) => {
			const normalized = normalizeAttachment(node);
			return normalized ? [normalized] : [];
		});
	}, [data]);
	// Filter
	const filtered = useMemo(
		() =>
			allAttachments.filter(
				(att) =>
					matchesPatterns(att.fileName, resolved.patterns) &&
					matchesLabels(att.labels, resolved.labels),
			),
		[allAttachments, resolved.patterns, resolved.labels],
	);

	// Sort client-side.
	const sorted = useMemo(
		() => sortAttachments(filtered, sortBy, sortOrder),
		[filtered, sortBy, sortOrder],
	);

	// Client-side pagination: derive totalPages from the full filtered list,
	// then slice to the current page. This matches legacy behavior and gives
	// us the true total upfront so all page numbers render immediately.
	const totalCount = sorted.length;
	const totalPages = Math.max(1, Math.ceil(totalCount / resolved.pageSize));
	const hasNextPage = currentPage < totalPages;

	// Reset to page 1 when pageSize or content changes.
	const prevContentId = useRef(effectiveContentId);
	const prevPageSize = useRef(resolved.pageSize);
	useEffect(() => {
		if (
			prevContentId.current !== effectiveContentId ||
			prevPageSize.current !== resolved.pageSize
		) {
			prevContentId.current = effectiveContentId;
			prevPageSize.current = resolved.pageSize;
			setCurrentPage(1);
		}
	}, [effectiveContentId, resolved.pageSize]);

	const safePage = Math.min(currentPage, totalPages);
	const pageStart = (safePage - 1) * resolved.pageSize;
	const attachments = sorted.slice(pageStart, pageStart + resolved.pageSize);

	const onSort = useCallback(
		(column: AttachmentSortBy) => {
			if (column === sortBy) {
				setSortOrder((prev) => (prev === 'ascending' ? 'descending' : 'ascending'));
			} else {
				setSortBy(column);
				setSortOrder('ascending');
			}
			setCurrentPage(1);
		},
		[sortBy],
	);

	// Combine cross-page and main query loading/error states.
	const combinedLoading = loading || (isCrossPage && titleQuery.loading);
	const combinedError =
		error ??
		titleQuery.error ??
		(crossPageNotFound ? new Error('attachments-macro: cross-page target not found') : null);

	return {
		attachments,
		totalCount,
		currentPage: safePage,
		totalPages,
		hasNextPage,
		pageId,
		dspBlocksDownloads,
		hasAttachFilePermission,
		loading: combinedLoading,
		error: combinedError,
		sortBy,
		sortOrder,
		onSort,
		onPageChange: setCurrentPage,
		showVersionHistory: resolved.showVersionHistory,
		showUpload: resolved.showUpload,
		showPreview: resolved.showPreview,
		refetch,
	};
};
